import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { useSequenceDetailsQuery } from '~/data';
import {
  useContentReady,
  shouldEnableContentReadyTracking
} from '~/lib/useContentReady';
import { Sequence as SequenceDef, useTrainingStore } from '../state';
import { useTrainingParams } from '../useTrainingParams';
import { useSequenceStore } from './SequenceStore';
import { SequenceAnswersProvider } from './SequenceAnswers';
import { translate } from './templates';
import { Root } from './templates/items/root/Root';

interface SequenceContentProps {
  sequence: SequenceDef;
}

/**
 * Renders the right sequence component based on the type
 */
export const SequenceContent = ({ sequence }: SequenceContentProps) => {
  const setAutoContinue = useSequenceStore(s => s.setAutoContinue);
  const setPersonalization = useSequenceStore(s => s.setPersonalization);
  const companyName = useTrainingStore(s => s.companyName);
  const logoUrl = useTrainingStore(s => s.logoUrl);
  const featureShowLlama = useTrainingStore(s => s.featureShowLlama);

  const { learnerTrainingId, sequenceId } = useTrainingParams();
  const { data, loading } = useSequenceDetailsQuery({
    variables: {
      learnerTrainingUuid: learnerTrainingId!,
      chapterSequenceId: sequence.chapterSequenceId
    }
  });

  const RootComponent = useMemo(() => {
    return (
      data?.sequence?.id === sequence.id &&
      new Root(
        translate({
          ...data.sequence,
          companyName: companyName,
          logoUrl: logoUrl,
          featureShowLlama: featureShowLlama
        })
      ).component()
    );
  }, [
    data,
    sequence.id,
    learnerTrainingId,
    companyName,
    logoUrl,
    featureShowLlama
  ]);

  useEffect(() => {
    if (!data) return;

    // TODO: change setPersonalization logic
    setPersonalization(data?.sequence?.personalization || false);

    if (data?.sequence?.templateName?.substring(0, 4) === 'Quiz') {
      setAutoContinue(false);
    } else {
      // setAutoContinue(data.sequence.autoContinue || false);
      setAutoContinue(false);
    }
  }, [data]);

  const shouldTrackContentReady = shouldEnableContentReadyTracking();
  const isActive = sequence.id === sequenceId;
  const isContentReady = useContentReady(
    shouldTrackContentReady && isActive,
    RootComponent,
    !!RootComponent && !loading
  );
  useLayoutEffect(() => {
    if (!shouldTrackContentReady || !isActive) return;

    const root = document.querySelector('main#root');
    root?.classList.toggle('content-ready', isContentReady);
    return () => {
      root?.classList.remove('content-ready');
    };
  }, [isContentReady, shouldTrackContentReady, isActive]);

  return RootComponent ? (
    <SequenceAnswersProvider value={sequence.chapterSequenceId}>
      <RootComponent key={sequence.id} />
    </SequenceAnswersProvider>
  ) : null;
};
